import { useAccountStore } from '@/store/account.store';
import { usePaywallStore, type PaywallFeature } from '@/store/paywall.store';

/**
 * Centralised paid-feature gate.
 *
 * `isPaywalled` is true when the cached account is loaded, isn't on a
 * paid plan, and isn't an admin. The `account !== null` guard prevents
 * the gate from flashing during the initial auth bootstrap (we don't
 * want to render a "locked" state for a moment, then flip to unlocked
 * once `/accounts/me` resolves).
 *
 * Mirrors the upgrade-pill predicate in `BrandHeader` so admins keep
 * full access for testing — the server-side `AdminGuard` independently
 * enforces admin scopes on writes regardless of what this returns.
 */
export function usePaywall() {
  const isPaidSubscriber = useAccountStore((s) => s.isPaidSubscriber());
  const isAdmin = useAccountStore((s) => s.isAdmin());
  const account = useAccountStore((s) => s.account);
  const openPaywall = usePaywallStore((s) => s.openPaywall);

  const isPaywalled = account !== null && !isPaidSubscriber && !isAdmin;

  return {
    isPaywalled,
    openPaywall: (feature: PaywallFeature) => openPaywall(feature),
  };
}
